import { useState, useEffect } from 'react';
import { Modal, ModalActions } from '../common/Modal';
import {
  useAiSettings,
  useUpdateAiSettings,
  useDeleteAiSettings,
  useTestAiSettings,
} from '../../hooks';
import type { AiProvider } from '../../types';

interface AiSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PROVIDERS: {
  id: AiProvider;
  name: string;
  model: string;
  cost: string;
  costPerEstimate: string;
  keyUrl: string;
  keyLabel: string;
}[] = [
  {
    id: 'anthropic',
    name: 'Anthropic',
    model: 'Claude Opus 4.6',
    cost: '$5 / $25 per 1M tokens',
    costPerEstimate: '~$0.05 / stima',
    keyUrl: 'https://console.anthropic.com/settings/keys',
    keyLabel: 'Anthropic Console',
  },
  {
    id: 'gemini',
    name: 'Google',
    model: 'Gemini 2.5 Pro',
    cost: '$1.25 / $10 per 1M tokens',
    costPerEstimate: '~$0.02 / stima',
    keyUrl: 'https://aistudio.google.com/apikey',
    keyLabel: 'Google AI Studio',
  },
  {
    id: 'kimi',
    name: 'Moonshot',
    model: 'Kimi K2.5',
    cost: '$0.60 / $2.50 per 1M tokens',
    costPerEstimate: '~$0.01 / stima',
    keyUrl: 'https://platform.moonshot.cn/console/api-keys',
    keyLabel: 'Moonshot Platform',
  },
];

export function AiSettingsModal({ isOpen, onClose }: AiSettingsModalProps) {
  const { data: settings, isLoading } = useAiSettings();
  const updateMutation = useUpdateAiSettings();
  const deleteMutation = useDeleteAiSettings();
  const testMutation = useTestAiSettings();

  const [selectedProvider, setSelectedProvider] = useState<AiProvider>('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setSelectedProvider(settings.provider);
    }
  }, [settings]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setApiKey('');
      setTestResult(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    setTestResult(null);
    await updateMutation.mutateAsync({
      provider: selectedProvider,
      apiKey: apiKey || undefined,
    });
    setApiKey('');
    onClose();
  };

  const handleDelete = async () => {
    setTestResult(null);
    await deleteMutation.mutateAsync();
    setApiKey('');
  };

  const handleTest = async () => {
    setTestResult(null);
    // Save first if there's a new key, then test
    if (apiKey) {
      await updateMutation.mutateAsync({
        provider: selectedProvider,
        apiKey,
      });
      setApiKey('');
    } else if (selectedProvider !== settings?.provider) {
      await updateMutation.mutateAsync({ provider: selectedProvider });
    }
    const result = await testMutation.mutateAsync();
    setTestResult(result);
  };

  const isSaving = updateMutation.isPending;
  const isTesting = testMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const isBusy = isSaving || isTesting || isDeleting;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Impostazioni AI" size="lg">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          {/* Provider selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Provider AI</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setSelectedProvider(p.id);
                    setTestResult(null);
                  }}
                  disabled={isBusy}
                  className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                    selectedProvider === p.id
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="font-semibold text-gray-900 text-sm">{p.name}</div>
                  <div className="text-xs text-gray-600 mt-1">{p.model}</div>
                  <div className="text-xs text-gray-400 mt-2">{p.cost}</div>
                  <div className={`text-xs font-medium mt-2 ${selectedProvider === p.id ? 'text-blue-600' : 'text-gray-500'}`}>
                    {p.costPerEstimate}
                  </div>
                  {selectedProvider === p.id && (
                    <div className="absolute top-2 right-2">
                      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* API Key input */}
          <div className="mt-6 space-y-2">
            <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700">
              API Key
            </label>
            <input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setTestResult(null);
              }}
              placeholder={settings?.hasApiKey ? '••••••••••••  (chiave salvata)' : 'Inserisci la tua API key'}
              disabled={isBusy}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            />
            <div className="flex items-center justify-between">
              <a
                href={PROVIDERS.find((p) => p.id === selectedProvider)?.keyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Ottieni API key da {PROVIDERS.find((p) => p.id === selectedProvider)?.keyLabel}
              </a>
              {settings?.hasApiKey && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isBusy}
                  className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                >
                  {isDeleting ? 'Rimuovendo...' : 'Rimuovi chiave'}
                </button>
              )}
            </div>
            {settings?.hasFallback && !settings?.hasApiKey && (
              <p className="text-xs text-gray-500">
                Un provider di fallback (Anthropic) e configurato dal server. Puoi usarlo senza inserire una chiave.
              </p>
            )}
          </div>

          {/* Test result */}
          {testResult && (
            <div
              className={`mt-4 p-3 rounded-lg text-sm ${
                testResult.success
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {testResult.success ? 'OK' : 'Errore'}: {testResult.message}
            </div>
          )}

          <ModalActions>
            <button
              type="button"
              onClick={handleTest}
              disabled={isBusy}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {isTesting ? 'Test in corso...' : 'Testa connessione'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isBusy || (!apiKey && selectedProvider === settings?.provider)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? 'Salvando...' : 'Salva'}
            </button>
          </ModalActions>
        </>
      )}
    </Modal>
  );
}
